<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import PublicSiteFooter from '@/components/publicSite/PublicSiteFooter.vue'
import PublicSiteHeader from '@/components/publicSite/PublicSiteHeader.vue'
import { getLocalizedModuleGuides, normalizeModuleGuideLocale } from '@/data/moduleGuides.localized'
import { usePublicPricingPlans } from '@/composables/usePublicPricingPlans'

type PublicLocale = 'it' | 'en' | 'fr' | 'ar'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const { t, locale } = useI18n({ useScope: 'global' })
const publicLocale = computed(() => normalizeModuleGuideLocale(locale.value) as PublicLocale)
const { plans, fetchPlans } = usePublicPricingPlans()

const features = computed(() => getLocalizedModuleGuides(locale.value))

const stats = computed(() => [
  { value: '200+', label: t('publicSite.landing.statsApps') },
  { value: '50k+', label: t('publicSite.landing.statsUsers') },
  { value: '99.9%', label: t('publicSite.landing.statsUptime') },
])

const setupSEO = () => {
  if (typeof document === 'undefined') return

  const title = t('publicSite.landing.seoTitle')
  const description = t('publicSite.landing.seoDescription')
  const url = window.location.origin
  const image = `${url}/images/sito-seo.jpg`

  // Title
  document.title = title

  // Helper to update or create meta tags
  const setMeta = (name: string, content: string, isProperty = false) => {
    let el = document.querySelector(`meta[${isProperty ? 'property' : 'name'}="${name}"]`)
    if (!el) {
      el = document.createElement('meta')
      if (isProperty) el.setAttribute('property', name)
      else el.setAttribute('name', name)
      document.head.appendChild(el)
    }
    el.setAttribute('content', content)
  }

  setMeta('description', description)
  
  // Open Graph
  setMeta('fb:app_id', '631115267646423', true)
  setMeta('og:title', title, true)
  setMeta('og:description', description, true)
  setMeta('og:image', image, true)
  setMeta('og:image:alt', t('publicSite.landing.ogImageAlt'), true)
  setMeta('og:url', url, true)
  setMeta('og:type', 'website', true)

  // Twitter
  setMeta('twitter:card', 'summary_large_image')
  setMeta('twitter:title', title)
  setMeta('twitter:description', description)
  setMeta('twitter:image', image)
}

onMounted(() => {
  fetchPlans()
  setupSEO()
})

watch(locale, () => {
  setupSEO()
})
</script>

<template>
  <div class="landing-premium">
    <!-- Navbar -->
    <PublicSiteHeader />

    <!-- Hero Section -->
    <header class="hero-section">
      <div class="container hero-grid">
        <div class="hero-text animate-fade-in">
          <div class="badge mb-6">✨ {{ t('publicSite.landing.heroBadge') }}</div>
          <h1 class="text-h1 hero-title mb-6">
            {{ t('publicSite.landing.heroTitle') }} <br/> 
            <span class="gradient-text">{{ t('publicSite.landing.heroTitleAccent') }}</span>
          </h1>
          <p class="text-h5 hero-description mb-10 leading-relaxed">
            {{ t('publicSite.landing.heroDescription') }}
          </p>
          <div class="d-flex flex-wrap gap-4">
            <VBtn size="x-large" color="primary" to="/register" class="btn-glow px-10 py-4 text-h6 font-weight-bold">
              {{ t('publicSite.shared.startFree') }}
            </VBtn>
            <VBtn size="x-large" variant="outlined" color="white" href="#features" class="px-10 py-4 text-h6 font-weight-bold">
              {{ t('publicSite.shared.exploreModules') }}
            </VBtn>
          </div>
        </div>
        <div class="hero-visual animate-float">
          <div class="glow-sphere"></div>
          <img src="/storage/branding/hero-premium.png" :alt="t('publicSite.landing.ogImageAlt')" class="main-mockup" />
        </div>
      </div>
    </header>

    <!-- Stats Section -->
    <section id="stats" class="stats-bar">
      <div class="container d-flex justify-space-around flex-wrap gap-12">
        <div v-for="s in stats" :key="s.label" class="stat-item">
          <span class="stat-value">{{ s.value }}</span>
          <span class="stat-label">{{ s.label }}</span>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="features-section py-16">
      <div class="container">
        <div class="section-header text-center mb-16">
          <h2 class="text-h2 font-weight-black section-title mb-4">{{ t('publicSite.landing.featuresTitle') }}</h2>
          <p class="text-h6 section-lead max-w-600 mx-auto">
            {{ t('publicSite.landing.featuresDescription') }}
          </p>
        </div>

        <VRow>
          <VCol v-for="f in features" :key="f.title" cols="12" md="6" lg="3">
            <div class="feature-glass-card">
              <div class="feature-badge" :style="{ color: f.color, backgroundColor: `${f.color}22` }">{{ f.eyebrow }}</div>
              <div class="icon-wrap" :style="{ backgroundColor: f.color + '22', color: f.color }">
                <VIcon :icon="f.icon" size="32" />
              </div>
              <h3 class="text-h5 font-weight-bold feature-title mb-3">{{ f.title }}</h3>
              <p class="text-body-1 feature-summary mb-5">{{ f.summary }}</p>
              <div class="feature-card-actions">
                <VBtn
                  :to="`/modules/${f.slug}`"
                  variant="text"
                  color="white"
                  class="text-none px-0 font-weight-bold feature-link-btn"
                >
                  {{ t('publicSite.shared.openTenantGuide') }}
                  <VIcon icon="tabler-arrow-right" end size="18" />
                </VBtn>
              </div>
            </div>
          </VCol>
        </VRow>
      </div>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="pricing-section py-16">
      <div class="container">
        <div class="section-header text-center mb-16">
          <h2 class="text-h2 font-weight-black section-title mb-4">{{ t('publicSite.landing.pricingTitle') }}</h2>
          <p class="text-h6 section-lead max-w-600 mx-auto">
            {{ t('publicSite.landing.pricingDescription') }}
          </p>
        </div>

        <VRow justify="center" align="stretch">
          <VCol v-for="p in plans" :key="p.name" cols="12" md="4" class="d-flex">
            <div class="pricing-card" :class="{ 'popular-card': p.popular }">
              <div v-if="p.popular" class="popular-badge">{{ t('publicSite.landing.popularBadge') }}</div>
              <div class="pa-8">
                <h3 class="text-h4 font-weight-bold plan-title mb-2">{{ p.name }}</h3>
                <p class="text-body-2 plan-description mb-6">{{ p.desc }}</p>
                
                <div class="d-flex align-baseline mb-8">
                  <span class="text-h3 font-weight-black text-white">€{{ p.price }}</span>
                  <span class="text-body-1 plan-period ms-2">{{ t('publicSite.landing.perMonth') }}</span>
                </div>

                <VDivider class="border-white-opacity-10 mb-8" />

                <ul class="feature-list mb-10">
                  <li v-for="feat in p.features" :key="feat" class="d-flex align-center mb-4">
                    <VIcon icon="tabler-circle-check" color="primary" size="20" class="me-3" />
                    <span class="text-body-2 plan-feature-text">{{ feat }}</span>
                  </li>
                </ul>

                <VBtn 
                  block 
                  size="x-large" 
                  :variant="p.popular ? 'elevated' : 'outlined'" 
                  :color="p.popular ? 'primary' : 'white'"
                  class="font-weight-bold text-none rounded-xl"
                  :class="{ 'btn-glow': p.popular }"
                >
                  {{ p.button }}
                </VBtn>
              </div>
            </div>
          </VCol>
        </VRow>
      </div>
    </section>

    <!-- Big CTA -->
    <section class="cta-banner">
      <div class="container text-center py-16">
        <div class="cta-inner glass-effect pa-16 rounded-xl overflow-hidden position-relative">
          <div class="bg-gradient-circles"></div>
          <h2 class="text-h2 font-weight-black cta-title mb-8 position-relative">
            {{ t('publicSite.landing.ctaTitle') }}
          </h2>
          <p class="text-h5 cta-description mb-12 position-relative">
            {{ t('publicSite.landing.ctaDescription') }}
          </p>
          <VBtn size="x-large" color="white" class="text-primary px-16 py-6 text-h6 font-weight-bold position-relative" to="/register">
            {{ t('publicSite.shared.createYourAppNow') }}
          </VBtn>
        </div>
      </div>
    </section>

    <PublicSiteFooter />
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800&display=swap');

.landing-premium {
  font-family: 'Outfit', sans-serif;
  background-color: #0F172A;
  color: #F1F5F9;
  min-height: 100vh;
  overflow-x: hidden;
  --text-strong: #FFF;
  --text-body: #E5EEF9;
  --text-soft: #E2E8F0;
  --text-muted: #CBD5E1;
}

.container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 40px;
}

/* Hero Section */
.hero-section {
  padding-top: 100px;
  padding-bottom: 100px;
  position: relative;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  align-items: center;
  gap: 60px;
}

.hero-title {
  color: var(--text-strong);
  text-shadow: 0 8px 30px rgba(15, 23, 42, 0.35);
}

.hero-description {
  color: var(--text-body);
}

.badge {
  display: inline-block;
  padding: 8px 16px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 100px;
  color: #A5B4FC;
  font-weight: 600;
  font-size: 14px;
}

.gradient-text {
  background: linear-gradient(135deg, #818CF8 0%, #C084FC 50%, #6366F1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
}

.hero-visual {
  position: relative;
  display: flex;
  justify-content: center;
}

.main-mockup {
  width: 100%;
  max-width: 500px;
  height: auto;
  border-radius: 40px;
  box-shadow: 0 50px 100px -20px rgba(0, 0, 0, 0.5);
  position: relative;
  z-index: 2;
}

.glow-sphere {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 140%;
  height: 140%;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
  filter: blur(80px);
  z-index: 1;
}

/* Stats */
.stats-bar {
  background: rgba(255, 255, 255, 0.02);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding: 40px 0;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.stat-value {
  font-size: 42px;
  font-weight: 800;
  color: var(--text-strong);
}

.stat-label {
  font-size: 14px;
  color: var(--text-soft);
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 600;
}

.section-title {
  color: var(--text-strong);
}

.section-lead {
  color: var(--text-soft);
}

/* Feature Cards */
.feature-glass-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 24px;
  padding: 40px;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.4s ease;
  cursor: pointer;
}

.feature-glass-card:hover {
  background: rgba(255, 255, 255, 0.06);
  transform: translateY(-12px);
  border-color: rgba(99, 102, 241, 0.3);
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.3);
}

.icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.feature-badge {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin-bottom: 18px;
}

.feature-card-actions {
  margin-top: auto;
}

.feature-link-btn {
  min-height: unset !important;
}

.feature-title {
  color: var(--text-strong);
}

.feature-summary {
  color: var(--text-soft);
}

/* Pricing Cards */
.pricing-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 32px;
  width: 100%;
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.pricing-card:hover {
  transform: translateY(-10px);
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

.popular-card {
  background: rgba(99, 102, 241, 0.08);
  border: 2px solid #6366F1;
  transform: scale(1.05);
  z-index: 2;
}

.popular-card:hover {
  transform: scale(1.08) translateY(-10px);
  background: rgba(99, 102, 241, 0.12);
}

.popular-badge {
  background: #6366F1;
  color: white;
  font-size: 12px;
  font-weight: 800;
  padding: 6px 16px;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 0 0 12px 12px;
  letter-spacing: 1px;
}

.feature-list {
  list-style: none;
  padding: 0;
}

.plan-title {
  color: var(--text-strong);
}

.plan-description,
.plan-feature-text {
  color: var(--text-soft);
}

.plan-period {
  color: var(--text-muted);
}

/* CTA Banner */
.cta-inner {
  background: #1E293B;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.cta-title {
  color: var(--text-strong);
}

.cta-description {
  color: var(--text-body);
}

.bg-gradient-circles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 10% 20%, rgba(99, 102, 241, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 90% 80%, rgba(168, 85, 247, 0.1) 0%, transparent 50%);
  pointer-events: none;
}

/* Footer */
/* Utilities & Animations */
.btn-glow {
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.3);
  transition: all 0.3s ease;
}

.btn-glow:hover {
  box-shadow: 0 0 30px rgba(99, 102, 241, 0.6);
  transform: scale(1.05);
}

.animate-fade-in {
  animation: fadeIn 1s ease-out forwards;
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

@media (max-width: 960px) {
  .hero-grid {
    grid-template-columns: 1fr;
    text-align: center;
  }
  .d-flex.gap-4 { justify-content: center; }
  .popular-card,
  .popular-card:hover {
    transform: none;
  }
}

@media (max-width: 600px) {
  .container { padding: 0 20px; }
  /* Sopra i 960px .text-h1 resta il default del tema (2.875rem): qui sotto
     va SOLO ridotto ulteriormente per i telefoni piu' stretti, mai
     ingrandito - prima di questo fix un @media (max-width: 960px) forzava
     3.5rem, piu' grande del default desktop, rendendo il titolo hero
     sproporzionato proprio sugli schermi piu' piccoli. */
  .text-h1.hero-title { font-size: 2.25rem !important; line-height: 1.15 !important; }
  .feature-glass-card { padding: 24px; }
  .cta-inner.pa-16 { padding: 32px !important; }
}
</style>
